import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useB2C } from "@/context/B2CAuthContext";
import { useLang } from "@/context/LanguageContext";

const IDLE_MS = 30 * 60 * 1000; // 30 minutes
const WARN_MS = 60 * 1000; // show a 1-minute countdown before logout
const CHECK_MS = 1000;
const ACTIVITY_KEY = "findo_last_activity"; // shared across tabs of the same origin
const EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];

const now = () => Date.now();
function readActivity() {
  try {
    const v = parseInt(localStorage.getItem(ACTIVITY_KEY) || "0", 10);
    return Number.isFinite(v) && v > 0 ? v : now();
  } catch { return now(); }
}
function writeActivity(ts) {
  try { localStorage.setItem(ACTIVITY_KEY, String(ts)); } catch { /* ignore */ }
}

// Auto-logout B2C & B2B users after 30 min of *cross-tab* inactivity, with a
// 1-minute warning countdown so the user can keep their session alive.
export default function IdleLogoutGuard() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { partner, logout: logoutB2B } = useAuth();
  const { user, logout: logoutB2C } = useB2C();
  const firedRef = useRef(false);
  const warnedRef = useRef(false);

  // Reset trackers when the logged-in identity changes.
  useEffect(() => {
    firedRef.current = false;
    warnedRef.current = false;
    writeActivity(now());
  }, [partner, user]);

  useEffect(() => {
    const dismissWarn = () => {
      if (warnedRef.current) { toast.dismiss("idle-warn"); warnedRef.current = false; }
    };
    const bump = () => {
      writeActivity(now());
      dismissWarn();
    };
    EVENTS.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    // Another tab recorded activity → clear our pending warning.
    const onStorage = (e) => { if (e.key === ACTIVITY_KEY) dismissWarn(); };
    window.addEventListener("storage", onStorage);

    const timer = setInterval(() => {
      const isB2C = !!user;
      const isB2B = !!partner;
      if (!isB2C && !isB2B) return;
      const idle = now() - readActivity();

      if (idle >= IDLE_MS) {
        if (firedRef.current) return;
        firedRef.current = true;
        toast.dismiss("idle-warn");
        if (isB2C) logoutB2C?.();
        if (isB2B) logoutB2B?.();
        toast.error(t("hc.sessionExpired"), {
          duration: 8000,
        });
        navigate(isB2B ? "/auth/login?expired=1" : "/mon-compte?expired=1");
        return;
      }

      if (idle >= IDLE_MS - WARN_MS) {
        const secs = Math.max(1, Math.ceil((IDLE_MS - idle) / 1000));
        warnedRef.current = true;
        toast.warning(t("hc.stillThere"), {
          id: "idle-warn",
          description: t("hc.idleLogoutIn").replace("{n}", secs),
          duration: WARN_MS + 2000,
          action: {
            label: t("hc.staySignedIn"),
            onClick: () => { writeActivity(now()); toast.dismiss("idle-warn"); warnedRef.current = false; },
          },
        });
      }
    }, CHECK_MS);

    return () => {
      EVENTS.forEach((e) => window.removeEventListener(e, bump));
      window.removeEventListener("storage", onStorage);
      clearInterval(timer);
    };
  }, [partner, user, logoutB2B, logoutB2C, navigate]);

  return null;
}
